import { useState } from 'react'
import Delete from '@mui/icons-material/Delete'
import Key from '@mui/icons-material/Key'
import KeyOff from '@mui/icons-material/KeyOff'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import { QueryKey } from '@tanstack/react-query'
import useOverlay from '@/hooks/useOverlay'
import {
  useDeleteOrgCredential,
  useUpdateOrgCredential,
} from '@/service-library/hooks/org-credentials'
import queryKeys from '@/service-library/query-keys'
import { getCredential } from '@/services/credentials'
import { useGetQBOCompanyInfo } from '@/services/hooks/relay-qbo-hooks'
import { AuthType, OrgCredential, OrgCredentialType } from '@/types/credentials'
import {
  isAuthenticated,
  authLabelMap,
  editableAuthTypes,
} from '@/utils/org-credentials'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import CopyIDButton from '@/components/copy-id-button/CopyIDButton'
import VendorRollUpLineItemsSwitch from '@/components/integration-settings/VendorRollUpLineItemsSwitch'
import ManageCredentialAuthDialog from './ManageCredentialAuthDialog'

type CredentialCardProps = {
  credential: OrgCredential
  listQueryKey: QueryKey
  type: OrgCredentialType
  onClick: (credential: OrgCredential) => void
  selected?: boolean
}

export default function CredentialCard({
  credential,
  listQueryKey,
  type,
  onClick,
  selected,
}: CredentialCardProps) {
  const { isAuthorized: canEditOrgs } = useStytchIsAuthorized('pd.org', 'edit')
  const manageAuthOverlay = useOverlay()
  const [authTypeForDialog, setAuthTypeForDialog] = useState<
    AuthType | undefined
  >(undefined)
  const [oauthUrlIsOpening, setOauthUrlIsOpening] = useState(false)

  const authenticated = isAuthenticated({ ...credential, type })

  const { deleteOrgCredential } = useDeleteOrgCredential({
    listQueryKey,
    sideEffectQueryKeys: [queryKeys.orgCredentials.all],
    onSuccess: () => {
      showSuccessSnackbar('Credential Deleted')
    },
    onError: () => {
      showErrorSnackbar('Failed to delete credentials. Please contact support.')
    },
  })

  const { updateOrgCredential } = useUpdateOrgCredential({
    listQueryKey,
    sideEffectQueryKeys: [['vendor_settings']],
    onError: () => {
      showErrorSnackbar('Failed to update default. Please try again.')
    },
  })

  const { companyInfo } = useGetQBOCompanyInfo({
    orgId: credential.org_id,
    enabled: type.code === 'qbo' && authenticated,
  })

  function handleAuthenticate() {
    setOauthUrlIsOpening(true)
    getCredential(type.trigger_url, credential.id).then(({ data }) => {
      setOauthUrlIsOpening(false)
      window.open(data.auth_url, '_blank')
    })
  }

  const hideAuthButtons =
    type.code === 'entrata_api_token' || type.auth_type[0] === 'app_xchange'

  const showDefaults =
    selected && type.allowed_configuration.includes('vendor_roll_up')

  return (
    <Card>
      <CardActionArea
        sx={{
          p: 2,
          background: selected
            ? (theme) => theme.palette.action.selected
            : undefined,
          borderLeft: (theme) =>
            selected
              ? `solid 4px ${theme.palette.primary.main}`
              : 'solid 4px transparent',
        }}
        onClick={() => onClick(credential)}
        component="div"
      >
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            {authenticated ? (
              <Key fontSize="large" />
            ) : (
              <KeyOff fontSize="large" color="secondary" />
            )}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6">{credential.org?.name || ''}</Typography>
              {type.code === 'qbo' && companyInfo && (
                <Typography variant="body2" color="text.secondary">
                  Company: {companyInfo.company_name}
                </Typography>
              )}
            </Stack>
          </Stack>

          <Stack spacing={1} direction="row" alignItems="center">
            {canEditOrgs &&
              !hideAuthButtons &&
              type.auth_type.map((authType) => {
                if (editableAuthTypes.includes(authType)) {
                  return (
                    <Button
                      key={authType}
                      color={credential.needs_secrets ? 'secondary' : 'neutral'}
                      sx={{ width: 'fit-content' }}
                      variant="text"
                      onClick={(e) => {
                        setAuthTypeForDialog(authType)
                        manageAuthOverlay.open(e)
                      }}
                    >
                      {credential.needs_secrets ? 'Set' : 'Edit'}{' '}
                      {authLabelMap[authType]}
                    </Button>
                  )
                }
                return (
                  <LoadingButton
                    loading={oauthUrlIsOpening}
                    disabled={oauthUrlIsOpening}
                    key={authType}
                    color={credential.needs_auth ? 'secondary' : 'neutral'}
                    variant="text"
                    onClick={handleAuthenticate}
                    sx={{ width: 'fit-content' }}
                  >
                    {credential.needs_auth ? 'Authenticate' : 'Re-authenticate'}
                  </LoadingButton>
                )
              })}

            <CopyIDButton stringToCopy={credential.id} asIconButton />
            {canEditOrgs && (
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={() => {
                    deleteOrgCredential(credential.id)
                  }}
                  size="small"
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {canEditOrgs && authTypeForDialog && (
          <ManageCredentialAuthDialog
            authType={authTypeForDialog}
            isEdit={!credential.needs_secrets}
            overlay={manageAuthOverlay}
            orgCredential={credential}
            type={type}
            onSave={() => {
              setAuthTypeForDialog(undefined)
            }}
          />
        )}
      </CardActionArea>

      <Collapse in={canEditOrgs && showDefaults} unmountOnExit>
        <Box
          sx={{
            p: 1.5,
          }}
        >
          <VendorRollUpLineItemsSwitch
            forCredential
            checked={credential.roll_up_line_items_default}
            onChange={(checked) => {
              updateOrgCredential({
                ...credential,
                roll_up_line_items_default: checked,
              })
            }}
          />
        </Box>
      </Collapse>
    </Card>
  )
}
